import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore, isClientPortalRole } from '../store/authStore';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import { useSidebar } from '../contexts/SidebarContext';
import DarkModeToggle from './DarkModeToggle';
import { TooltipProvider } from './ui/Tooltip';
import { useToast } from '../hooks/useToast';
import api from '../api';
import { adminAPI, portalBrandingAPI } from '../api';
import { DEFAULT_TENANT_MENU_ITEMS } from '../lib/tenantMenuItems';
import {
  LayoutDashboard,
  Building2,
  FileText,
  Users,
  Receipt,
  ShoppingCart,
  Wallet,
  TrendingUp,
  CheckSquare,
  Settings,
  LogOut,
  Menu,
  X,
  UserCog,
  Palette,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Wrench,
  Calculator,
  PoundSterling,
  Paintbrush,
  ShoppingBag,
  CreditCard,
  Zap,
  Flame,
  Shield,
  Camera,
  Briefcase,
  Car,
  Plus,
  List,
  Calendar,
  MessageSquare,
  Server,
  Package,
  AlertTriangle,
  Inbox,
  BookOpen,
} from 'lucide-react';

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { hasUnsavedChanges } = useUnsavedChanges();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const { error: showErrorToast, ToastContainer } = useToast();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [menuOrder, setMenuOrder] = useState(null);
  const [portalBranding, setPortalBranding] = useState(null);
  const [portalLogoUrl, setPortalLogoUrl] = useState(null);
  const [newErrorsCount, setNewErrorsCount] = useState(0);
  const portalLogoObjectUrlRef = useRef(null);
  const hasShownNewErrorsToastRef = useRef(false);
  
  // Helper function to check if path is active
  const isActive = (path) => {
    // Exact match for dashboards
    if (path === '/admin/dashboard' || path === '/tenant/dashboard' || path === '/client/dashboard') {
      return location.pathname === path;
    }
    
    // Exact match
    if (location.pathname === path) {
      return true;
    }
    
    // Check if path is a prefix, but exclude cases where we're on a /new or similar route
    // that should only match its exact path
    if (location.pathname.startsWith(path)) {
      const remainingPath = location.pathname.slice(path.length);
      
      // If remaining path is empty, it's an exact match (already handled above)
      if (!remainingPath) {
        return true;
      }
      
      // If remaining path starts with /new, don't match (e.g., /tenant/purchases shouldn't match /tenant/purchases/new)
      if (remainingPath.startsWith('/new')) {
        return false;
      }
      
      // Allow matching for detail/edit routes (e.g., /tenant/invoices/123 should match /tenant/invoices)
      // UUID pattern or numeric ID
      const idPattern = /^\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const numericPattern = /^\/\d+$/;
      
      if (idPattern.test(remainingPath) || numericPattern.test(remainingPath)) {
        return true;
      }
    }
    
    return false;
  };

  const handleLogout = () => {
    const slug = user?.portalSlug;
    const role = user?.role;
    logout();
    if (isClientPortalRole(role) && slug) {
      navigate(`/${slug}/logon`, { replace: true });
    } else {
      navigate('/login');
    }
  };

  // Icon mapping
  const iconMap = {
    dashboard: LayoutDashboard,
    tenants: Building2,
    invoices: FileText,
    clients: Users,
    estimates: Receipt,
    purchases: ShoppingCart,
    suppliers: Building2,
    banking: Wallet,
    reports: TrendingUp,
    certificates: CheckSquare,
    certification: CheckSquare,
    settings: Settings,
    team: UserCog,
    visual: Palette,
    tools: Wrench,
    calculators: Calculator,
    design: Paintbrush,
    pricing: PoundSterling,
    sales: ShoppingBag,
    financial: CreditCard,
    electrical: Zap,
    fire: Flame,
    security: Shield,
    cctv: Camera,
    jobs: Briefcase,
    fleet: Car,
    create: Plus,
    view: List,
    planner: Calendar,
    rams: Shield,
    feedback: MessageSquare,
    errors: AlertTriangle,
    server: Server,
    package: Package,
    clientMessages: Inbox,
    knowledgeBase: BookOpen,
  };

  // Role-based navigation items
  const getNavItems = () => {
    const role = user?.role;

    if (role === 'global_admin') {
      return [
        { path: '/admin/dashboard', label: 'Dashboard', iconKey: 'dashboard', enabled: true },
        { path: '/admin/visual-settings', label: 'Visual Settings', iconKey: 'visual', enabled: true },
        { path: '/admin/tenants', label: 'Tenants', iconKey: 'tenants', enabled: true },
        { path: '/admin/feedback', label: 'Feedback', iconKey: 'feedback', enabled: true },
        { path: '/admin/errors', label: 'Errors', iconKey: 'errors', enabled: true },
        {
          path: '/admin/server-admin',
          label: 'Server Admin',
          iconKey: 'server',
          enabled: true,
          subItems: [
            { path: '/admin/dependencies', label: 'Dependencies', iconKey: 'package', enabled: true },
          ],
        },
      ];
    }

    if (isClientPortalRole(role)) {
      return [
        { path: '/client/dashboard', label: 'Dashboard', iconKey: 'dashboard', enabled: true },
        { path: '/client/invoices', label: 'Invoices', iconKey: 'invoices', enabled: true },
        { path: '/client/estimates', label: 'Estimates', iconKey: 'estimates', enabled: true },
        { path: '/client/certificates', label: 'Certificates', iconKey: 'certificates', enabled: true },
        { path: '/client/statement', label: 'Statement', iconKey: 'reports', enabled: true },
        { path: '/client/account', label: 'My account', iconKey: 'team', enabled: true },
        { path: '/client/messages', label: 'Message us', iconKey: 'feedback', enabled: true },
      ];
    }

    // Build tenant items from shared source (lib/tenantMenuItems.js)
    if (role === 'tenant_admin') {
      const items = DEFAULT_TENANT_MENU_ITEMS.map((item) => {
        // Filter RAMS from Tools if not enabled for this tenant
        if (item.path === '/tenant/tools' && item.subItems && !user?.ramsEnabled) {
          return {
            ...item,
            subItems: item.subItems.filter((si) => si.path !== '/tenant/tools/rams'),
          };
        }
        return { ...item };
      });

      return applyMenuOrder(items);
    }

    // tenant_user: exclude Tools
    const items = DEFAULT_TENANT_MENU_ITEMS.filter((item) => item.path !== '/tenant/tools');

    return applyMenuOrder(items);
  };

  /**
   * Applies custom menu order to navItems (tenant roles only).
   * - Missing items: paths in custom order but not in current role's menu are ignored.
   * - New items: items in menu but not in custom order appear at end in default order.
   * - Sub-items: order is applied from saved menu order, including reordered sub-items
   *   and sub-items moved between parents.
   * - Validation: ensures parent-child relationships are valid (sub-items exist in default menu).
   * - Fallback: if no custom order, returns items unchanged (default order).
   * @param {Array<{ path: string, label: string, subItems?: Array }>} items - Default nav items
   * @returns {Array} Nav items in custom or default order
   */
  const applyMenuOrder = (items) => {
    if (!menuOrder || menuOrder.length === 0) {
      return items;
    }

    // Dashboard always first (non-draggable per plan)
    const dashboardItem = items.find(item => item.path === '/tenant/dashboard');
    const otherItems = items.filter(item => item.path !== '/tenant/dashboard');

    // Create maps for quick lookup
    const pathToItem = Object.fromEntries(otherItems.map(item => [item.path, item]));
    
    // Create a global map of ALL sub-items across all default parents
    // This allows us to find sub-items that have been moved to different parents
    const globalSubItemMap = new Map(); // path -> subItem object
    const subItemPathToDefaultParent = new Map(); // path -> default parent path
    
    otherItems.forEach(parent => {
      if (parent.subItems) {
        parent.subItems.forEach(subItem => {
          globalSubItemMap.set(subItem.path, subItem);
          subItemPathToDefaultParent.set(subItem.path, parent.path);
        });
      }
    });

    const ordered = [];
    const seenParentPaths = new Set();
    const seenSubItemPaths = new Set();

    // Process custom order (can be old format: strings, or new format: objects with subItems)
    for (const orderItem of menuOrder) {
      let parentPath;
      let subItemsOrder = null;

      if (typeof orderItem === 'string') {
        // Legacy format: just a path string
        parentPath = orderItem;
      } else if (orderItem && typeof orderItem === 'object' && orderItem.path) {
        // New format: { path, label?: string, subItems: [...] }
        parentPath = orderItem.path;
        subItemsOrder = orderItem.subItems || null;
      } else {
        continue; // Skip invalid entries
      }

      const defaultItem = pathToItem[parentPath];
      if (!defaultItem || seenParentPaths.has(parentPath)) {
        continue; // Skip if not in current role's menu or duplicate
      }

      // Create a copy of the item
      const orderedItem = { ...defaultItem };
      
      // Apply custom label if provided
      if (typeof orderItem === 'object' && orderItem.label) {
        orderedItem.label = orderItem.label;
      }

      // Apply sub-items order if provided
      if (subItemsOrder && Array.isArray(subItemsOrder)) {
        orderedItem.subItems = [];
        
        // Process sub-items in custom order (may include moved sub-items)
        for (const subItemOrder of subItemsOrder) {
          const subItemPath = typeof subItemOrder === 'string' ? subItemOrder : subItemOrder?.path;
          
          if (!subItemPath) {
            continue; // Skip invalid sub-item entries
          }

          // Validation: Check if sub-item exists in the global map (valid sub-item)
          const subItem = globalSubItemMap.get(subItemPath);
          if (!subItem) {
            // Invalid sub-item path - skip it (orphaned sub-item)
            continue;
          }

          // Validation: Ensure parent-child relationship is valid
          // A sub-item can be moved to any parent, but we validate it exists in the menu
          // The backend handles more strict validation, but we do basic validation here
          if (!seenSubItemPaths.has(subItemPath)) {
            const subItemWithLabel = { ...subItem };
            // Apply custom label if provided
            if (typeof subItemOrder === 'object' && subItemOrder.label) {
              subItemWithLabel.label = subItemOrder.label;
            }
            orderedItem.subItems.push(subItemWithLabel);
            seenSubItemPaths.add(subItemPath);
          }
        }

        // Add any remaining default sub-items that weren't in the custom order
        // (but only if the parent originally had sub-items)
        if (defaultItem.subItems) {
          defaultItem.subItems.forEach(defaultSubItem => {
            if (!seenSubItemPaths.has(defaultSubItem.path)) {
              orderedItem.subItems.push(defaultSubItem);
              seenSubItemPaths.add(defaultSubItem.path);
            }
          });
        }
      } else if (defaultItem.subItems) {
        // No custom sub-items order, keep default order
        orderedItem.subItems = [...defaultItem.subItems];
        // Mark all default sub-items as seen
        defaultItem.subItems.forEach(subItem => {
          seenSubItemPaths.add(subItem.path);
        });
      }

      ordered.push(orderedItem);
      seenParentPaths.add(parentPath);
    }

    // New items: not in custom order, append at end in default order
    for (const item of otherItems) {
      if (!seenParentPaths.has(item.path)) {
        // For items not in custom order, ensure their sub-items are marked as seen
        // to prevent duplicates if they were moved elsewhere
        if (item.subItems) {
          item.subItems.forEach(subItem => {
            if (!seenSubItemPaths.has(subItem.path)) {
              seenSubItemPaths.add(subItem.path);
            }
          });
        }
        ordered.push(item);
      }
    }

    return dashboardItem ? [dashboardItem, ...ordered] : ordered;
  };

  const navItems = getNavItems();

  // Auto-expand menus with active sub-items (accordion: only one open at a time)
  useEffect(() => {
    const newExpandedMenus = {};
    navItems.forEach(item => {
      if (item.subItems && item.subItems.some(subItem => isActive(subItem.path))) {
        newExpandedMenus[item.path] = true;
      }
    });
    // Only keep the menu with active sub-items open (accordion behavior)
    setExpandedMenus(newExpandedMenus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user?.role]);

  // Fetch portal branding (tenant logo + company name) when user is client portal
  useEffect(() => {
    if (!isClientPortalRole(user?.role)) {
      setPortalBranding(null);
      setPortalLogoUrl(null);
      if (portalLogoObjectUrlRef.current) {
        URL.revokeObjectURL(portalLogoObjectUrlRef.current);
        portalLogoObjectUrlRef.current = null;
      }
      return;
    }
    let cancelled = false;
    portalBrandingAPI
      .getBranding()
      .then((data) => {
        if (cancelled) return;
        setPortalBranding({ companyName: data.companyName || null, logoUrl: data.logoUrl || null });
        if (data.logoUrl) {
          api.get('/portal/logo', { responseType: 'blob' })
            .then((res) => {
              if (cancelled) return;
              if (portalLogoObjectUrlRef.current) {
                URL.revokeObjectURL(portalLogoObjectUrlRef.current);
              }
              const url = URL.createObjectURL(res.data);
              portalLogoObjectUrlRef.current = url;
              setPortalLogoUrl(url);
            })
            .catch(() => {
              if (!cancelled) setPortalLogoUrl(null);
            });
        } else {
          setPortalLogoUrl(null);
        }
      })
      .catch(() => {
        if (!cancelled) setPortalBranding(null);
      });
    return () => {
      cancelled = true;
      if (portalLogoObjectUrlRef.current) {
        URL.revokeObjectURL(portalLogoObjectUrlRef.current);
        portalLogoObjectUrlRef.current = null;
      }
      setPortalLogoUrl(null);
    };
  }, [user?.role]);

  // Fetch new errors count for global_admin (badge + toast on first load)
  useEffect(() => {
    if (user?.role !== 'global_admin') {
      setNewErrorsCount(0);
      return;
    }
    let cancelled = false;
    adminAPI
      .getErrorsNewCount()
      .then((data) => {
        if (cancelled) return;
        const count = data?.count ?? data ?? 0;
        setNewErrorsCount(count);
        if (count > 0 && !hasShownNewErrorsToastRef.current) {
          showErrorToast(
            'New errors reported',
            `${count} new error ${count === 1 ? 'group' : 'groups'} need attention.`
          );
          hasShownNewErrorsToastRef.current = true;
        }
      })
      .catch(() => {
        if (!cancelled) setNewErrorsCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.role, showErrorToast, location.pathname]);

  // Fetch menu order from API (only for tenant roles)
  // Refetch when navigating to tenant routes so changes saved in Admin Visual Settings propagate
  useEffect(() => {
    const role = user?.role;
    // Only fetch for tenant_admin and tenant_user roles
    if (role !== 'tenant_admin' && role !== 'tenant_user') {
      return;
    }

    let cancelled = false;
    adminAPI
      .getMenuOrder()
      .then((res) => {
        if (cancelled) return;
        // API returns { order: [...] } or the order array directly
        const order = Array.isArray(res) ? res : (res?.order || []);
        setMenuOrder(order.length > 0 ? order : null);
      })
      .catch(() => {
        // Silently fail - endpoint might not be available or user doesn't have access
        // Fall back to default order
        if (!cancelled) {
          setMenuOrder(null);
        }
      });
    
    return () => {
      cancelled = true;
    };
  }, [user?.role, location.pathname]);

  const handleNavClick = (path) => {
    // Check if current page has unsaved changes
    if (hasUnsavedChanges()) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to leave? Your changes will be lost.');
      if (!confirmed) {
        return;
      }
    }
    navigate(path);
    setIsMobileOpen(false);
  };

  const toggleSubmenu = (path) => {
    setExpandedMenus(prev => {
      const isCurrentlyOpen = prev[path];
      // If clicking to open, close all others and open this one
      // If clicking to close, just close this one
      if (!isCurrentlyOpen) {
        return { [path]: true };
      } else {
        return { [path]: false };
      }
    });
  };

  return (
    <TooltipProvider>
      <ToastContainer />
      {/* Mobile menu button */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-[var(--ds-surface-panel)] rounded-[var(--ds-radius-md)] shadow-[var(--ds-shadow-card)] border border-[var(--ds-border-subtle)] text-[var(--ds-text-primary)]"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed left-0 top-0 h-full bg-[var(--ds-surface-panel)] shadow-[var(--ds-shadow-card)] border-r border-[var(--ds-border-subtle)] flex flex-col z-40
          transform transition-all duration-180 ease-[cubic-bezier(0.2,0.8,0.2,1)]
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isCollapsed ? 'w-14' : 'w-64'}
        `}
      >
        {/* Logo/Brand */}
        <div className={`${isCollapsed ? 'px-2' : 'px-6'} py-4 border-b border-[var(--ds-border-divider)] flex justify-between items-center relative`}>
          {!isCollapsed ? (
            <div className="flex flex-col min-w-0 items-center">
              {(!isClientPortalRole(user?.role) || portalLogoUrl) && (
                <img
                  src={isClientPortalRole(user?.role) ? portalLogoUrl : '/sparxmate-logo.png'}
                  alt={isClientPortalRole(user?.role) && portalBranding?.companyName ? portalBranding.companyName : 'SparxMate'}
                  className="h-11 w-auto object-contain object-center"
                />
              )}
              <p className={`text-meta text-[var(--ds-text-muted)] text-center ${(!isClientPortalRole(user?.role) || portalLogoUrl) ? 'mt-1' : ''}`}>
                {isClientPortalRole(user?.role) && portalBranding?.companyName ? portalBranding.companyName : 'Trades Management'}
              </p>
            </div>
          ) : (
            (!isClientPortalRole(user?.role) || portalLogoUrl) && (
              <img
                src={isClientPortalRole(user?.role) ? portalLogoUrl : '/sparxmate-logo.png'}
                alt={isClientPortalRole(user?.role) && portalBranding?.companyName ? portalBranding.companyName : 'SparxMate'}
                className="h-8 max-w-[2.5rem] w-auto object-contain object-left flex-shrink-0"
              />
            )
          )}
          <div className={`flex items-center gap-2 flex-shrink-0 ${isCollapsed ? 'justify-center w-full' : ''}`}>
            <DarkModeToggle />
            <button
              onClick={() => setIsMobileOpen(false)}
              className="lg:hidden text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)] transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {/* Collapse Toggle Button - positioned on the right edge */}
          <button
            onClick={toggleSidebar}
            className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-[var(--ds-surface-panel)] border border-[var(--ds-border-subtle)] rounded-full shadow-[var(--ds-shadow-card)] items-center justify-center hover:bg-[var(--ds-control-field-hover-bg)] transition-colors z-50"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4 text-[var(--ds-text-secondary)]" />
            ) : (
              <ChevronLeft className="h-4 w-4 text-[var(--ds-text-secondary)]" />
            )}
          </button>
        </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3">
        <ul className="space-y-0.5 px-2">
          {navItems.map((item) => {
            const IconComponent = iconMap[item.iconKey] || LayoutDashboard;
            const active = isActive(item.path) && item.enabled;
            const hasSubItems = item.subItems && item.subItems.length > 0;
            const isExpanded = expandedMenus[item.path];
            const hasActiveSubItem = hasSubItems && item.subItems.some(subItem => isActive(subItem.path));

            return (
              <li key={item.path}>
                {hasSubItems ? (
                  <>
                    <button
                      onClick={() => !isCollapsed && toggleSubmenu(item.path)}
                      disabled={!item.enabled}
                      className={`
                        w-full flex items-center ${isCollapsed ? 'justify-center px-2' : 'px-3'} py-2.5 rounded-[var(--ds-radius-md)] text-left transition-colors duration-180
                        ${active || hasActiveSubItem
                          ? 'bg-[var(--ds-control-field-bg)] text-[var(--ds-accent-primary)]'
                          : item.enabled
                          ? 'text-[var(--ds-text-secondary)] hover:bg-[var(--ds-control-field-bg)] hover:text-[var(--ds-text-primary)]'
                          : 'text-[var(--ds-text-disabled)] cursor-not-allowed opacity-60'
                        }
                      `}
                      title={isCollapsed ? item.label : ''}
                    >
                      <IconComponent className={`${isCollapsed ? '' : 'mr-3'} h-5 w-5 flex-shrink-0 ${active || hasActiveSubItem ? 'text-[var(--ds-accent-primary)]' : ''}`} />
                      {!isCollapsed && (
                        <>
                          <span className="flex-1 text-body font-medium">{item.label}</span>
                          <ChevronDown className={`h-4 w-4 transition-transform duration-180 ${isExpanded ? 'rotate-180' : ''}`} />
                        </>
                      )}
                    </button>
                    {!isCollapsed && isExpanded && item.subItems && (
                      <>
                        <ul className="mt-0.5 space-y-0.5 ml-2">
                          {item.subItems.map((subItem) => {
                            const subActive = isActive(subItem.path) && subItem.enabled;
                            return (
                              <li key={subItem.path}>
                                <button
                                  onClick={() => subItem.enabled && handleNavClick(subItem.path)}
                                  disabled={!subItem.enabled}
                                  className={`
                                    w-full flex items-center px-3 py-2 rounded-[var(--ds-radius-md)] text-left transition-colors duration-180
                                    ${subActive
                                      ? 'bg-[var(--ds-control-field-bg)] text-[var(--ds-accent-primary)]'
                                      : subItem.enabled
                                      ? 'text-[var(--ds-text-secondary)] hover:bg-[var(--ds-control-field-bg)] hover:text-[var(--ds-text-primary)]'
                                      : 'text-[var(--ds-text-disabled)] cursor-not-allowed opacity-60'
                                    }
                                  `}
                                >
                                  <span className="flex-1 text-[12px] font-medium">{subItem.label}</span>
                                  {!subItem.enabled && (
                                    <span className="text-micro text-[var(--ds-text-muted)]">Soon</span>
                                  )}
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                        <hr className="my-2 border-[var(--ds-border-divider)]" />
                      </>
                    )}
                  </>
                ) : (
                  <button
                    onClick={() => item.enabled && handleNavClick(item.path)}
                    disabled={!item.enabled}
                    className={`
                      w-full flex items-center ${isCollapsed ? 'justify-center px-2' : 'px-3'} py-2.5 rounded-[var(--ds-radius-md)] text-left transition-colors duration-180
                      ${active
                        ? 'bg-[var(--ds-control-field-bg)] text-[var(--ds-accent-primary)]'
                        : item.enabled
                        ? 'text-[var(--ds-text-secondary)] hover:bg-[var(--ds-control-field-bg)] hover:text-[var(--ds-text-primary)]'
                        : 'text-[var(--ds-text-disabled)] cursor-not-allowed opacity-60'
                      }
                    `}
                    title={isCollapsed ? item.label : ''}
                  >
                    <span className="relative inline-flex">
                      <IconComponent className={`${isCollapsed ? '' : 'mr-3'} h-5 w-5 flex-shrink-0 ${active ? 'text-[var(--ds-accent-primary)]' : ''}`} />
                      {item.path === '/admin/errors' && newErrorsCount > 0 && (
                        <span className={`absolute -top-1 -right-1 min-w-[1rem] h-4 px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-medium ${isCollapsed ? 'top-0 right-0' : ''}`}>
                          {newErrorsCount > 99 ? '99+' : newErrorsCount}
                        </span>
                      )}
                    </span>
                    {!isCollapsed && (
                      <>
                        <span className="flex-1 text-body font-medium">{item.label}</span>
                        {!item.enabled && (
                          <span className="text-micro text-[var(--ds-text-muted)]">Soon</span>
                        )}
                      </>
                    )}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User Info & Logout */}
      <div className="border-t border-[var(--ds-border-divider)] p-4">
        {!isCollapsed && (
          <div className="mb-3 px-3">
            <p className="text-body font-medium text-[var(--ds-text-primary)]">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-meta text-[var(--ds-text-muted)] capitalize mt-0.5">
              {user?.role?.replace('_', ' ')}
            </p>
          </div>
        )}
        <button
          onClick={handleLogout}
          className={`w-full flex items-center ${isCollapsed ? 'justify-center px-2' : 'px-3'} py-2 rounded-[var(--ds-radius-md)] text-[var(--ds-status-danger)] hover:bg-[var(--ds-status-danger-bg)] transition-colors duration-180`}
          title={isCollapsed ? 'Logout' : ''}
        >
          <LogOut className={`${isCollapsed ? '' : 'mr-3'} h-5 w-5 flex-shrink-0`} />
          {!isCollapsed && <span className="text-body font-medium">Logout</span>}
        </button>
      </div>
    </aside>
    </TooltipProvider>
  );
}

